import React from 'react';
import Head from 'next/head';

import { ProductDetailsQuery$data } from '__generated__/ProductDetailsQuery.graphql';
import {
  BASE_PATH,
  DOMAIN,
  PRODUCT_DETAILS_PAGE,
} from 'shared/constants/paths.constants';
import { Segment } from 'shared/hooks';
import {
  checkIsBusinessSegment,
  segmentPathMap,
} from 'shared/utils/segments.utils';

interface SchemaTagsProps {
  product: ProductDetailsQuery$data['productData'];
  segment: Segment;
}

type ProductVariant =
  ProductDetailsQuery$data['productData']['offerDetails'][0];

export const getFullUrl = (segment: Segment, groupId: string): string => {
  const segmentPath = segmentPathMap[segment] ?? '';
  return `${DOMAIN}${BASE_PATH}${segmentPath}${PRODUCT_DETAILS_PAGE}/${groupId}`;
};

const getPriceSpecification = (segment: Segment, variant: ProductVariant) => {
  if (checkIsBusinessSegment(segment))
    return {
      price: variant.basePriceExcludingTax,
      priceSpecification: {
        '@type': 'PriceSpecification',
        price: variant.basePriceExcludingTax,
        valueAddedTaxIncluded: 'https://schema.org/False',
      },
    };

  return {
    price: variant.basePrice,
    priceSpecification: {
      '@type': 'PriceSpecification',
      price: variant.basePrice,
      valueAddedTaxIncluded: 'https://schema.org/True',
    },
  };
};

const getAdditionalProperties = (variant: ProductVariant) => {
  const properties = [];
  if (variant.storage) {
    properties.push({
      '@type': 'PropertyValue',
      name: 'capacity',
      value: variant.storage,
    });
  }
  return properties;
};

export const getStockAvailability = ({
  isAvailableForStore,
  isAvailableOnline,
  isPreOrderable,
}: {
  isAvailableForStore: boolean;
  isAvailableOnline: boolean;
  isPreOrderable: boolean;
}) => {
  if (isPreOrderable) {
    return 'https://schema.org/PreOrder';
  }
  if (isAvailableForStore && isAvailableOnline) {
    return 'https://schema.org/InStock';
  }
  if (isAvailableForStore && !isAvailableOnline) {
    return 'https://schema.org/InStoreOnly';
  }
  if (!isAvailableForStore && isAvailableOnline) {
    return 'https://schema.org/OnlineOnly';
  }

  return 'https://schema.org/OutOfStock';
};

const getProductFields = (variant: ProductVariant, segment: Segment) => {
  const image = variant?.images?.[0]?.defaultUrl;
  const { stock, preOrderable: isPreOrderable } = variant;
  const isAvailableForStore = stock?.storeStatus === 'IN_STOCK';
  const isAvailableOnline = stock?.warehouseStatus === 'IN_STOCK';
  return {
    '@type': 'Product',
    name: variant.name,
    description: variant.description,
    inProductGroupWithID: variant.groupId,
    color: variant.color,
    sku: variant.externalId,
    additionalProperty: getAdditionalProperties(variant),
    image: [image],
    url: `${getFullUrl(segment, variant.groupId)}?offerId=${variant.id}`,
    brand: {
      '@type': 'Brand',
      name: variant.brand,
    },
    offers: {
      '@type': 'Offer',
      availability: getStockAvailability({
        isAvailableForStore,
        isAvailableOnline,
        isPreOrderable,
      }),
      itemCondition: 'https://schema.org/NewCondition',
      priceCurrency: 'NZD',
      ...getPriceSpecification(segment, variant),
    },
  };
};

export const getSchemaMarkup = ({ product, segment }: SchemaTagsProps) => {
  const variants = product.offerDetails.map((variant) =>
    getProductFields(variant, segment),
  );

  // We use the first product to access the values which are common across the group
  const firstVariant = product?.offerDetails[0];

  return {
    '@context': 'https://schema.org/',
    '@type': 'ProductGroup',
    name: firstVariant.groupName,
    // category: '', This can be provided as a google category ID and will override what Google determines automatically
    description: firstVariant.groupDescription,
    productGroupId: firstVariant.groupId,
    image: firstVariant.images?.[0]?.defaultUrl,
    brand: {
      '@type': 'Brand',
      name: firstVariant.brand,
    },
    hasVariant: variants,
  };
};

const SchemaTags = ({ product, segment }: SchemaTagsProps) => {
  if (!product) return null;

  const markup = getSchemaMarkup({ product, segment });

  return (
    <Head>
      <script
        data-testid="schema-markup"
        type="application/ld+json"
        // See: https://nextjs.org/learn/seo/rendering-and-ranking/metadata
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(markup) }}
      />
    </Head>
  );
};

export default SchemaTags;
